import React, { useMemo } from "react";
import { Card, Col, Placeholder } from "react-bootstrap";
import { useFetchAbsencesQuery } from "features/absences/absenceSlice";
import { useFetchPaiementsQuery } from "features/paiements/paiementSlice";
import { useFetchEvenementQuery } from "features/evenements/evenementSlice";

interface ActivityItem {
  type: "absence" | "paiement" | "evenement";
  label: string;
  date: Date;
  icon: string;
  color: string;
}

const formatRelative = (d: Date) => {
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Il y a ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `Il y a ${diffD}j`;
  return d.toLocaleDateString("fr-TN", { day: "2-digit", month: "short" });
};

const RecentActivity = () => {
  const { data: absences = [], isLoading: loadAbs } = useFetchAbsencesQuery();
  const { data: paiements = [], isLoading: loadPai } = useFetchPaiementsQuery();
  const { data: evenements = [], isLoading: loadEvt } = useFetchEvenementQuery();

  const isLoading = loadAbs || loadPai || loadEvt;

  const activities = useMemo(() => {
    const items: ActivityItem[] = [];

    absences.forEach((a) => {
      if (a.date) {
        items.push({
          type: "absence",
          label: `${a.eleves?.length || 0} absence(s) enregistrée(s)`,
          date: new Date(a.date),
          icon: "ri-user-unfollow-line",
          color: "danger",
        });
      }
    });

    paiements.forEach((p) => {
      if (p.date_paiement) {
        items.push({
          type: "paiement",
          label: `Paiement de ${parseFloat(p.montant) || 0} DT`,
          date: new Date(p.date_paiement),
          icon: "ri-money-dollar-circle-line",
          color: "success",
        });
      }
    });

    evenements.forEach((e) => {
      if (e.creation_date) {
        items.push({
          type: "evenement",
          label: e.titre || "Événement",
          date: new Date(e.creation_date),
          icon: "ri-calendar-event-line",
          color: "primary",
        });
      }
    });

    return items
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 8);
  }, [absences, paiements, evenements]);

  return (
    <Col xxl={8} lg={12}>
      <Card>
        <Card.Header>
          <h5 className="card-title mb-0">
            <i className="ri-time-line me-2 text-info" />
            Activité récente
          </h5>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <Placeholder as="div" animation="glow">
              {[1, 2, 3, 4].map((n) => (
                <Placeholder key={n} xs={12} className="mb-2" style={{ height: 28 }} />
              ))}
            </Placeholder>
          ) : activities.length === 0 ? (
            <div className="text-center text-muted py-3">
              Aucune activité récente
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {activities.map((a, i) => (
                <div
                  key={i}
                  className="d-flex align-items-center gap-3 p-2 rounded"
                  style={{ background: "var(--tb-light, #f8f9fa)" }}
                >
                  <div
                    className={`avatar-xs flex-shrink-0 rounded-circle bg-${a.color}-subtle d-flex align-items-center justify-content-center`}
                  >
                    <i className={`${a.icon} text-${a.color} fs-16`} />
                  </div>
                  <div className="flex-grow-1 overflow-hidden">
                    <p className="mb-0 text-truncate fs-14">{a.label}</p>
                  </div>
                  <span className="text-muted fs-12 flex-shrink-0">
                    {formatRelative(a.date)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default RecentActivity;
